import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import routeMeta from "@/seo/routeMeta.json";

// Single source of truth (shared with the build-time prerender script).
const HOME_DESCRIPTION = routeMeta.homeDescription;
const FALLBACK_TITLE = routeMeta.fallbackTitle;
const ROUTE_META = routeMeta.routes;
const ADMIN_META = routeMeta.admin;
const OG_IMAGE = routeMeta.ogImage;
const OG_SITE_NAME = routeMeta.ogSiteName;
const SITE_ORIGIN = routeMeta.siteOrigin;

const normalisePath = (pathname) => {
  if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname;
};

const upsertMeta = (name, content) => {
  const nodes = document.head.querySelectorAll(`meta[name="${name}"]`);
  let el = nodes[0];
  for (let i = 1; i < nodes.length; i += 1) nodes[i].remove();
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertMetaProp = (property, content) => {
  const nodes = document.head.querySelectorAll(`meta[property="${property}"]`);
  let el = nodes[0];
  for (let i = 1; i < nodes.length; i += 1) nodes[i].remove();
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href) => {
  const nodes = document.head.querySelectorAll('link[rel="canonical"]');
  let el = nodes[0];
  for (let i = 1; i < nodes.length; i += 1) nodes[i].remove();
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const removeCanonical = () => {
  document.head
    .querySelectorAll('link[rel="canonical"]')
    .forEach((node) => node.remove());
};

// Map a pathname to a human-friendly location label for analytics.
const pageLabel = (path) => {
  if (path === "/") return "home";
  return path.replace(/^\//, "");
};

export const RouteMetadata = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = normalisePath(pathname);

    let meta;
    let ogUrl;
    if (path === "/admin") {
      meta = ADMIN_META;
      ogUrl = ADMIN_META.ogUrl || `${SITE_ORIGIN}/admin`;
    } else if (ROUTE_META[path]) {
      meta = ROUTE_META[path];
      ogUrl = meta.canonical;
    } else {
      meta = {
        title: FALLBACK_TITLE,
        description: HOME_DESCRIPTION,
        robots: "noindex,nofollow",
      };
      ogUrl = SITE_ORIGIN + path;
    }

    document.title = meta.title;
    upsertMeta("description", meta.description);
    upsertMeta("robots", meta.robots);

    if (meta.canonical) {
      upsertCanonical(meta.canonical);
    } else {
      removeCanonical();
    }

    // Social preview tags (kept in sync on client-side navigation).
    upsertMetaProp("og:title", meta.title);
    upsertMetaProp("og:description", meta.description);
    upsertMetaProp("og:url", ogUrl);
    upsertMetaProp("og:type", "website");
    upsertMetaProp("og:site_name", OG_SITE_NAME);
    upsertMetaProp("og:image", OG_IMAGE);
    upsertMetaProp("og:image:width", "1200");
    upsertMetaProp("og:image:height", "630");
    upsertMeta("twitter:card", "summary_large_image");
  }, [pathname]);

  // Analytics: fire a GA event whenever a mailto link is clicked (any page).
  useEffect(() => {
    const handler = (e) => {
      const link = e.target && e.target.closest
        ? e.target.closest('a[href^="mailto:"]')
        : null;
      if (!link || typeof window.gtag !== "function") return;

      let loc = link.getAttribute("data-loc");
      if (!loc) {
        if (link.closest("footer")) loc = "footer";
        else if (link.closest("nav")) loc = "navbar";
        else if (link.closest('[data-testid="cta-band-button"]') || link.closest("[data-cta]")) loc = "cta";
        else loc = pageLabel(normalisePath(window.location.pathname));
      }
      window.gtag("event", "contact_email_click", { link_location: loc });
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  return null;
};
